import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { showToast } from "../toastService";
import { apiFetch } from "../utils/api";
import Header from "../components/Header";
import BottomNavbar from "../components/BottomNavbar";
import "./css/Notifications.css";

import noWorkImage from "../assets/images/no_work.png";

const USER_BASE_URL = process.env.REACT_APP_USER_BASE_URL;
const VERSION = process.env.REACT_APP_VERSION;
const LIMIT = 20;

export default function Notifications() {
  const navigate = useNavigate();
  const [lang] = useState(localStorage.getItem("app_language") || "en");
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(true);

  const observerTarget = useRef(null);

  // Translations
  const t = {
    en: {
      notifications: "Notifications",
      loading: "Loading",
      noNotifications: "No notifications available",
      loadingMore: "Loading more...",
    },
    ar: {
      notifications: "الإشعارات",
      loading: "جاري التحميل",
      noNotifications: "لا توجد إشعارات متاحة",
      loadingMore: "جاري تحميل المزيد...",
    },
  }[lang];

  // Fetch notifications
  const fetchNotifications = async (page = 1) => {
    try {
      if (page === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const data = await apiFetch(
        `${USER_BASE_URL}/${VERSION}/notification/?limit=${LIMIT}&page=${page}`,
        "GET",
        null,
        {}
      );

      if (data && data.response_code === 200 && data.response) {
        const newNotifications = data.response || [];
        
        if (page === 1) {
          setNotifications(newNotifications);
        } else {
          setNotifications((prev) => [...prev, ...newNotifications]);
        }

        setHasMorePages(newNotifications.length === LIMIT);
        setCurrentPage(page);

        // Mark notifications as viewed
        if (newNotifications.length > 0) {
          const unviewedIds = newNotifications
            .filter((notif) => !notif.viewed)
            .map((notif) => notif._id);

          if (unviewedIds.length > 0) {
            try {
              await apiFetch(
                `${USER_BASE_URL}/${VERSION}/notification/view`,
                "POST",
                JSON.stringify({ id: unviewedIds }),
                { "Content-Type": "application/json" }
              );
              // Update local state to mark as viewed
              setNotifications((prev) =>
                prev.map((notif) =>
                  unviewedIds.includes(notif._id)
                    ? { ...notif, viewed: true }
                    : notif
                )
              );
            } catch (err) {
              console.error("Error marking notifications as viewed:", err);
            }
          }
        }
      }
    } catch (err) {
      console.error("Error fetching notifications:", err);
      if (page === 1) {
        showToast(err.message || "Failed to load notifications", "error");
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Fetch notifications on mount
  useEffect(() => {
    fetchNotifications(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle infinite scroll
  const handleLoadMore = useCallback(() => {
    if (!loadingMore && hasMorePages && !loading) {
      fetchNotifications(currentPage + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingMore, hasMorePages, currentPage, loading]);

  // Infinite scroll effect
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMorePages && !loadingMore && !loading) {
          handleLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [handleLoadMore, hasMorePages, loadingMore, loading]);

  // Handle notification click
  const handleNotificationClick = (notification) => {
    if (notification.type === "route" && notification.route && notification.route_id) {
      navigate(`${notification.route}${notification.route_id}`);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffTime / (1000 * 60));

    if (diffDays > 7) {
      return date.toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } else if (diffDays > 0) {
      return `${diffDays} ${diffDays === 1 ? "day" : "days"} ago`;
    } else if (diffHours > 0) {
      return `${diffHours} ${diffHours === 1 ? "hour" : "hours"} ago`;
    } else if (diffMinutes > 0) {
      return `${diffMinutes} ${diffMinutes === 1 ? "minute" : "minutes"} ago`;
    } else {
      return lang === "ar" ? "الآن" : "Just now";
    }
  };

  // Loading screen
  if (loading && notifications.length === 0) {
    return (
      <div className="notifications-container" dir={lang === "ar" ? "rtl" : "ltr"}>
        <Header lang={lang} />
        <div className="notifications-loading">
          <div className="spinner"></div>
          <p>{t.loading}</p>
        </div>
        <BottomNavbar currentPage="settings" lang={lang} />
      </div>
    );
  }

  return (
    <div className="notifications-container" dir={lang === "ar" ? "rtl" : "ltr"}>
      {/* Header */}
      <Header lang={lang} />

      {/* Page Header */}
      <div className="notifications-page-header">
        <h1 className="notifications-page-title">{t.notifications}</h1>
      </div>

      {/* Main Content */}
      <div className="notifications-content">
        {notifications.length === 0 ? (
          <div className="notifications-empty">
            <img src={noWorkImage} alt="No notifications" />
            <p>{t.noNotifications}</p>
          </div>
        ) : (
          <>
            <div className="notifications-list">
              {notifications.map((notification) => (
                <div
                  key={notification._id}
                  className={`notification-item ${!notification.viewed ? "unviewed" : ""}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="notification-icon">
                    {notification.icon ? (
                      <img src={notification.icon} alt={notification.title} />
                    ) : (
                      <i className="bx bx-bell"></i>
                    )}
                  </div>
                  <div className="notification-content">
                    <div className="notification-header">
                      <h3 className="notification-title">{notification.title}</h3>
                      {!notification.viewed && (
                        <span className="notification-dot"></span>
                      )}
                    </div>
                    <p className="notification-message">{notification.message}</p>
                    <span className="notification-time">{formatDate(notification.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Loading more indicator */}
            {loadingMore && (
              <div className="notifications-loading-more">
                <div className="spinner"></div>
                <p>{t.loadingMore}</p>
              </div>
            )}

            {/* Observer target for infinite scroll */}
            {hasMorePages && !loadingMore && (
              <div ref={observerTarget} className="observer-target"></div>
            )}
          </>
        )}
      </div>

      {/* Bottom Navigation */}
      <BottomNavbar currentPage="" lang={lang} />
    </div>
  );
}

